import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";

const LEVEL = { established: "Well established", moderate: "Good but developing evidence", emerging: "Evidence still emerging" };

export const SourcesToggle = ({ sources, testId = "sources" }) => {
  const [open, setOpen] = useState(false);
  if (!sources?.length) return null;
  return (
    <div data-testid={testId}>
      <button type="button" data-testid={`${testId}-toggle`} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="font-mono inline-flex min-h-[48px] items-center gap-2 text-[15px] uppercase tracking-[0.22em] text-[#0B2E44]/80 transition-colors hover:text-[#0B2E44]">
        <motion.svg animate={{ rotate: open ? 90 : 0 }} viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></motion.svg>
        Sources ({sources.length})
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.ul data-testid={`${testId}-list`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ type: "spring", stiffness: 240, damping: 30 }} className="grid gap-2 overflow-hidden pt-2 sm:grid-cols-2">
            {sources.map((s, i) => (
              <motion.li key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-card-${s.id}`}
                  className="block rounded-xl bg-[#0B2E44]/[0.04] px-3.5 py-2.5 text-left ring-1 ring-[#0B2E44]/15 transition-[background-color,box-shadow] hover:bg-[#0B2E44]/[0.07] hover:ring-[#0F8A96]/40">
                  <span className="font-ui line-clamp-2 text-[15px] leading-snug text-[#0B2E44]">{s.title}</span>
                  <span className="font-mono mt-1 block truncate text-[15px] text-[#0B2E44]">{s.journal}{s.year ? ` · ${s.year}` : ""} ↗</span>
                </a>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ScienceReveal = ({ kind, targetId, align = "left", testId = "science", label = "See the science" }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(null);
  const [missing, setMissing] = useState(false);
  const toggle = async () => {
    if (!open && !note && !missing) {
      try { setNote((await api.get(`/science/${kind}/${targetId}`)).data); } catch { setMissing(true); }
    }
    setOpen((o) => !o);
  };
  return (
    <div data-testid={testId} className={align === "center" ? "text-center" : ""}>
      <button type="button" data-testid={`${testId}-toggle`} onClick={toggle} aria-expanded={open}
        className="font-ui inline-flex min-h-[48px] items-center gap-2 rounded-full px-3.5 text-[15px] text-[#0B2E44] ring-1 ring-[#0F8A96]/25 transition-[background-color,box-shadow] hover:bg-[#0B5C8A]/[0.06]">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="12" cy="12" r="2" /><ellipse cx="12" cy="12" rx="10" ry="4" /><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" /><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" /></svg>
        {open ? "Hide the science" : label}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div data-testid={`${testId}-panel`} initial={{ height: 0, opacity: 0, rotateX: -50 }} animate={{ height: "auto", opacity: 1, rotateX: 0 }} exit={{ height: 0, opacity: 0, rotateX: -30 }}
            transition={{ type: "spring", stiffness: 160, damping: 22 }} style={{ transformOrigin: "top", transformPerspective: 900 }} className="overflow-hidden">
            <div className="science-holo relative mt-3 rounded-[20px] p-5 text-left">
              <span aria-hidden className="science-scan pointer-events-none absolute inset-x-0 top-0 h-10" />
              {missing ? (
                <p className="font-ui text-[15px] text-[#0B2E44]">We haven't written up the research for this one yet. Ask the Biopeak team.</p>
              ) : note ? (
                <div className="space-y-3">
                  {note.sections?.length ? note.sections.filter((x) => x.text).map((x, i) => (
                    <div key={x.title} data-testid={`${testId}-section-${i}`}>
                      <h4 className="font-display text-xl leading-tight text-[#0B2E44]">{x.title}</h4>
                      <p className="font-ui mt-1 text-[15px] leading-relaxed text-[#0B2E44]">{x.text}</p>
                    </div>
                  )) : <p data-testid={`${testId}-body`} className="font-ui text-[15px] leading-relaxed text-[#0B2E44]">{note.body}</p>}
                  <p className="font-ui text-[15px] text-[#0B2E44]">{note.disclaimer}</p>
                </div>
              ) : <p className="font-ui text-[15px] text-[#0B2E44]">Loading…</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HEADINGS = new Set(["How to read this document", "A note on the calorie figures"]);

const Category = ({ c }) => {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button type="button" data-testid={`every-plate-cat-${c.key}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="font-ui flex min-h-[44px] w-full items-center justify-between gap-3 rounded-xl bg-white/80 px-4 text-left text-[15px] text-[#0B2E44] ring-1 ring-[#0B5C8A]/15 hover:ring-[#0B5C8A]/40">
        {c.title}
        <motion.svg animate={{ rotate: open ? 90 : 0 }} viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></motion.svg>
      </button>
      <AnimatePresence initial={false}>
        {open && <motion.p data-testid={`every-plate-intro-${c.key}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
          className="font-ui overflow-hidden px-4 pt-2 text-[15px] leading-relaxed text-[#0B2E44]">{c.intro}</motion.p>}
      </AnimatePresence>
    </li>
  );
};

export const EveryPlate = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const toggle = () => {
    setOpen((o) => !o);
    if (!data) api.get("/science/every-plate").then((r) => setData(r.data)).catch(() => setData({ paragraphs: [], categories: [] }));
  };
  const [title, ...rest] = data?.paragraphs || [];
  return (
    <div data-testid="every-plate">
      <button type="button" data-testid="every-plate-toggle" onClick={toggle} aria-expanded={open}
        className="font-ui inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white/80 px-4 text-[15px] text-[#0B2E44] ring-1 ring-[#0B5C8A]/25 transition-colors hover:bg-white">
        {open ? "Hide the science" : "The Science on Every Plate"}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div data-testid="every-plate-panel" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="science-holo relative mt-3 max-h-[60vh] space-y-3 overflow-y-auto rounded-[20px] p-5 text-left">
              {data ? (
                <>
                  <h3 data-testid="every-plate-title" className="font-display text-2xl leading-tight text-[#0B2E44]">{title}</h3>
                  {rest.map((p, i) => HEADINGS.has(p)
                    ? <h4 key={i} className="font-display pt-2 text-lg text-[#0B2E44]">{p}</h4>
                    : <p key={i} className="font-ui text-[15px] leading-relaxed text-[#0B2E44]">{p}</p>)}
                  <ul data-testid="every-plate-categories" className="space-y-2 pt-2">{data.categories.map((c) => <Category key={c.key} c={c} />)}</ul>
                </>
              ) : <p className="font-ui text-[15px] text-[#0B2E44]">Loading…</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
